'use client';

import React, { useState } from 'react';
import { Box, Text, VStack, HStack } from '@skin-seoul/design-system';
import LucideIcon from '@/components/ui/LucideIcon';
import ModalContainer from '../../../components/ModalContainer';
import { BENEFIT_DETAILS } from '@skin-seoul/service';
import { SafeLink } from '@/components/ui/SafeLink';
interface BenefitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  expandedBenefitId?: string;
}
const BenefitsModal = ({
  isOpen,
  onClose,
  expandedBenefitId
}: BenefitsModalProps) => {
  const [expandedBenefits, setExpandedBenefits] = useState<Set<string>>(expandedBenefitId ? new Set([expandedBenefitId]) : new Set(['authenticity']));
  const handleBenefitClick = (benefitId: string) => {
    const newExpandedBenefits = new Set(expandedBenefits);
    if (newExpandedBenefits.has(benefitId)) {
      newExpandedBenefits.delete(benefitId);
    } else {
      newExpandedBenefits.add(benefitId);
    }
    setExpandedBenefits(newExpandedBenefits);
  };
  const renderDescription = (description: string) => {
    const lines = description.split('\n');
    const elements: React.ReactNode[] = [];
    let currentBulletPoints: string[] = [];
    let currentParagraph = '';
    const flushBulletPoints = () => {
      if (currentBulletPoints.length > 0) {
        elements.push(<Box key={`bullets-${elements.length}`} as="ul" listStyleType="disc" paddingLeft={6} margin={0}>
            {currentBulletPoints.map((item, index) => <Box as="li" key={index} marginBottom={2}>
                <Text textStyle={{
              base: 'paragraph.small',
              tablet: 'paragraph.medium'
            }} color="neutral.600">
                  {item}
                </Text>
              </Box>)}
          </Box>);
        currentBulletPoints = [];
      }
    };
    const flushParagraph = () => {
      if (currentParagraph.trim()) {
        const paragraph = currentParagraph.trim();

        // Check if paragraph contains email
        if (paragraph.includes('support@skin-seoul.com')) {
          const parts = paragraph.split('support@skin-seoul.com');
          elements.push(<Text key={`para-${elements.length}`} textStyle={{
            base: 'paragraph.small',
            tablet: 'paragraph.medium'
          }} color="neutral.600">
              {parts[0]}
              <SafeLink href="mailto:support@skin-seoul.com" color="primary.400" textDecoration="underline" _hover={{
              color: 'primary.500'
            }}>
                support@skin-seoul.com
              </SafeLink>
              {parts[1]}
            </Text>);
        } else {
          elements.push(<Text key={`para-${elements.length}`} textStyle={{
            base: 'paragraph.small',
            tablet: 'paragraph.medium'
          }} color="neutral.600">
              {paragraph}
            </Text>);
        }
        currentParagraph = '';
      }
    };
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('•')) {
        // If we have a paragraph in progress, flush it first
        flushParagraph();
        // Add to bullet points
        currentBulletPoints.push(trimmedLine.substring(2).trim());
      } else if (trimmedLine === '') {
        // Empty line - could be paragraph separator
        if (currentBulletPoints.length > 0) {
          flushBulletPoints();
        } else if (currentParagraph) {
          flushParagraph();
        }
      } else {
        // Regular text line
        if (currentBulletPoints.length > 0) {
          flushBulletPoints();
        }
        // Add to current paragraph with space if needed
        if (currentParagraph && !currentParagraph.endsWith(' ')) {
          currentParagraph += ' ';
        }
        currentParagraph += trimmedLine;
      }
    });

    // Flush any remaining content
    flushBulletPoints();
    flushParagraph();

    // If no complex structure was found, return simple text
    if (elements.length === 0) {
      return <Text textStyle={{
        base: 'paragraph.small',
        tablet: 'paragraph.medium'
      }} color="neutral.600">
          {description}
        </Text>;
    }
    return <VStack gap={3} alignItems="flex-start" width="100%" data-sentry-element="VStack" data-sentry-component="renderDescription" data-sentry-source-file="BenefitsModal.tsx">
        {elements}
      </VStack>;
  };
  return <ModalContainer isOpen={isOpen} onClose={onClose} title="Only at SkinSeoul" data-sentry-element="ModalContainer" data-sentry-component="BenefitsModal" data-sentry-source-file="BenefitsModal.tsx">
      <VStack gap={6} width="100%" alignItems="flex-start" data-sentry-element="VStack" data-sentry-source-file="BenefitsModal.tsx">
        {BENEFIT_DETAILS.map(benefit => {
        const isExpanded = expandedBenefits.has(benefit.id);
        return <Box key={benefit.id} width="100%">
              <HStack justifyContent="space-between" alignItems="flex-start" width="100%" cursor="pointer" onClick={() => handleBenefitClick(benefit.id)}>
                <Text textStyle="label.large" color="neutral.800" fontWeight="medium" flex={1}>
                  {benefit.label}
                </Text>
                <LucideIcon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size="sm" color="neutral.600" transition="transform 0.2s" marginRight={{
              base: 0,
              tablet: 0
            }} />
              </HStack>

              {isExpanded && <Box paddingTop={4}>{renderDescription(benefit.description)}</Box>}
            </Box>;
      })}
      </VStack>
    </ModalContainer>;
};
export default BenefitsModal;